import React from "react";

const AdminRoutes = React.lazy(() => import("./AdminRoutes"));
const BusinessRoutes = React.lazy(() => import("./BusinessRoutes"));
const CustomerRoutes = React.lazy(() => import("./CustomerRoutes"));
const HomeRoutes = React.lazy(() => import("./HomeRoutes"));

type DomainRouterProps = {
  subdomain: string;
  domainParts: string[];
};

/**
 * Routes requests to the appropriate component based on the subdomain and domain parts.
 *
 * @param {DomainRouterProps} props - The router configuration properties
 * @returns {React.JSX.Element} The appropriate route component based on subdomain matching
 *
 * Routing logic:
 * - "admin" subdomain → AdminRoutes
 * - "customer" subdomain → CustomerRoutes
 * - "www", "workplanx" subdomains or single domain part → HomeRoutes
 * - All other subdomains → BusinessRoutes
 *
 * Subdomain matching is case-insensitive.
 */
const DomainRouter = (props: DomainRouterProps): React.JSX.Element => {
  const { subdomain, domainParts } = props;

  const loweredSubdomain = subdomain.toLowerCase();

  if (loweredSubdomain === "admin") {
    return <AdminRoutes />;
  }

  // auth flow (interface) can be reused in customer portal also
  if (loweredSubdomain === "customer") {
    return <CustomerRoutes />;
  }

  if (
    domainParts.length === 1 ||
    loweredSubdomain === "www" ||
    loweredSubdomain === "workplanx"
  ) {
    return <HomeRoutes />;
  }

  return <BusinessRoutes />;
};

export default DomainRouter;
